import {
  Box,
  Grid,
  GridItem,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Spinner,
  Center,
  Flex,
  Badge,
  Progress,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import axios from "axios";
import config from "../../../config";

const ActionSummary = ({
  data,
  error,
  isLoading,
  fetchActionBreakdown,
  component,
}) => {
  const themeColor = "#083c64";
  const accentColor = useColorModeValue("blue.400", "blue.300");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.600", "gray.300");

  if (isLoading) {
    return (
      <Box w="100%" p={6} bg={cardBg} borderRadius="2xl" boxShadow="lg">
        <Center h="300px">
          <Spinner size="xl" color={themeColor} thickness="4px" />
        </Center>
      </Box>
    );
  }

  if (error) {
    return (
      <Box w="100%" p={6} bg={cardBg} borderRadius="2xl" boxShadow="lg">
        <Center h="300px">
          <Text color="red.500" fontSize="lg">
            {error}
          </Text>
        </Center>
      </Box>
    );
  }

  const { totalCount, actionBreakdown } = data;

  // Calculate total actions per module
  const getModuleTotal = (actions) => {
    return Object.values(actions).reduce((sum, count) => sum + count, 0);
  };

  // Function to render action items for a module
  const renderActionItems = (actions) => {
    const moduleTotal = getModuleTotal(actions);

    return Object.entries(actions).map(([action, count]) => {
      const percentage =
        moduleTotal > 0 ? Math.round((count / moduleTotal) * 100) : 0;

      return (
        <Box key={action} w="100%" mb={3}>
          <Flex justify="space-between" mb={1}>
            <Text fontSize="sm" fontWeight="500" color={textColor}>
              {action
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
            </Text>
            <Text fontSize="sm" fontWeight="600">
              {count}
              <Text as="span" fontSize="xs" color={textColor} ml={1}>
                ({percentage}%)
              </Text>
            </Text>
          </Flex>
          <Progress
            value={percentage}
            size="xs"
            colorScheme="blue"
            borderRadius="full"
            bg={useColorModeValue("gray.100", "gray.600")}
          />
        </Box>
      );
    });
  };

  return (
    <Box w="100%" p={6} bg={cardBg} borderRadius="2xl" boxShadow="lg">
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={themeColor}>
            Activity Analytics
          </Text>
          <Text color={textColor} fontSize="sm">
            Overview of all user actions
          </Text>
        </Box>
        <Badge
          colorScheme="blue"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="sm"
        >
          Total: {totalCount} actions
        </Badge>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} >
        {Object.entries(actionBreakdown).map(([module, actions]) => {
          const moduleTotal = getModuleTotal(actions);
          return (
            <>{moduleTotal > 0 && <Stat
              key={module}
              p={4}
              bg={useColorModeValue("gray.50", "gray.700")}
              borderRadius="lg"
            >
              <StatLabel fontSize="sm" color={textColor}>
                {module.charAt(0).toUpperCase() + module.slice(1)}
              </StatLabel>
              <StatNumber fontSize="2xl" color={themeColor}>
                {moduleTotal}
              </StatNumber>
              {/* <StatHelpText> */}
              {/* <StatArrow type="increase" />
                23.36%
              </StatHelpText> */}
            </Stat>}</>
            
          );
        })}
      </SimpleGrid>
      <Grid  >{component}</Grid>
      <Grid
      style={{marginTop:"20px"}}
        templateColumns={{
          base: "1fr",
          md: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
        }}
        gap={6}
      >
        {actionBreakdown &&
          Object.entries(actionBreakdown).map(([module, actions]) => (
            <GridItem
              key={module}
              p={5}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="xl"
              bg={useColorModeValue("white", "gray.700")}
              boxShadow="md"
              _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
              transition="all 0.2s ease"
            >
              <Flex align="center" mb={4}>
                <Box w={3} h={3} bg={accentColor} borderRadius="full" mr={2} />
                <Text fontSize="lg" fontWeight="semibold" color={themeColor}>
                  {module.charAt(0).toUpperCase() + module.slice(1)}
                </Text>
                <Badge
                  colorScheme="blue"
                  ml="auto"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  fontSize="xs"
                >
                  {getModuleTotal(actions)} actions
                </Badge>
              </Flex>

              <VStack align="start" spacing={3}>
                {renderActionItems(actions)}
              </VStack>
            </GridItem>
          ))}
      </Grid>
    </Box>
  );
};

export default ActionSummary;
